// useStrokeTransform.js
import useStore from '../../store';

// Вспомогательные функции для вычислений
const calculateDistance = (point1, point2 = { clientX: 0, clientY: 0 }) => {
  const xDiff = (point1.x || point1.clientX) - (point2.x || point2.clientX);
  const yDiff = (point1.y || point1.clientY) - (point2.y || point2.clientY);
  return Math.sqrt(xDiff * xDiff + yDiff * yDiff);
};

const calculateCenter = (touch1, touch2 = { clientX: 0, clientY: 0 }) => {
  return {
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2,
  };
};

const calculateAngle = (touch1, touch2) => {
  const dx = touch2.pageX - touch1.pageX;
  const dy = touch2.pageY - touch1.pageY;
  return Math.atan2(dy, dx) * 180 / Math.PI;
};

const calculateLayerInvariantOffset = ({
  initialLayerOffset,      // Исходное смещение слоя
  initialLayerScale,       // Исходный масштаб слоя
  newLayerScale,           // Новый масштаб слоя
  initialLayerRotation,    // Исходный угол слоя
  newLayerRotation,        // Новый угол слоя
  pivotLayerPoint,         // Точка на слое (центр жеста в координатах слоя)
  offsetDelta,             // Дополнительное смещение от движения центра (в экранных координатах)
  layerCanvas,             // Канвас слоя для вычисления центра
}) => {
  const radInitial = (initialLayerRotation * Math.PI) / 180;
  const radNew = (newLayerRotation * Math.PI) / 180;

  // Центр слоя
  const centerX = layerCanvas.width / 2;
  const centerY = layerCanvas.height / 2;

  // Смещение точки относительно центра слоя
  const pivotRelative = {
    x: pivotLayerPoint.x - centerX,
    y: pivotLayerPoint.y - centerY,
  };

  // Преобразование точки pivot с учётом исходного поворота
  const rotatedInitial = {
    x: pivotRelative.x * Math.cos(radInitial) - pivotRelative.y * Math.sin(radInitial),
    y: pivotRelative.x * Math.sin(radInitial) + pivotRelative.y * Math.cos(radInitial),
  };

  // Преобразование точки pivot с учётом нового поворота
  const rotatedNew = {
    x: pivotRelative.x * Math.cos(radNew) - pivotRelative.y * Math.sin(radNew),
    y: pivotRelative.x * Math.sin(radNew) + pivotRelative.y * Math.cos(radNew),
  };

  // Вычисляем новое смещение
  return {
    x: initialLayerOffset.x + (initialLayerScale * rotatedInitial.x - newLayerScale * rotatedNew.x) + offsetDelta.x,
    y: initialLayerOffset.y + (initialLayerScale * rotatedInitial.y - newLayerScale * rotatedNew.y) + offsetDelta.y,
  };
};

export const useLayerTransform = (ref) => {
  const { menu, drawing, info, telegram, convy, brush, history, work } = ref;
  const { user } = info;
  const { showTemporaryHint } = menu.methods;
  const { zoom } = convy;

  // Функция получения настроек слоя (аналогично LayerMenu)
  const getLayerSettings = (layerId) => {
    const layer = convy.layerInfo[layerId];
    if (!layer) return {};
    return {
      groupId: layer.groupId || null,
      ...layer
    };
  };

  // Функция получения списка слоев для трансформации
  const getLayersForTransform = () => {
    const layersToTransform = [];

    if (convy.transformGroupId) {
      // Трансформируем группу - получаем все слои группы
      const gameSettings = info.game.settings || {};
      const layerGroups = gameSettings.layerGroups || {};
      
      if (layerGroups[convy.transformGroupId]) {
        // Находим все слои, принадлежащие этой группе (только основные слои, не маски)
        Object.keys(convy.layerInfo).forEach(layerId => {
          const layer = convy.layerInfo[layerId];
          const layerSettings = getLayerSettings(layerId);
          
          if (layerSettings.groupId === convy.transformGroupId && 
              !layer.deleted && 
              layer.visible && 
              !layer.clippingFor) { // Исключаем маски
            layersToTransform.push(layerId);
          }
        });
      }
    } else {
      // Трансформируем только активный слой
      const { getUserGameSets } = work.methods;
      const { activeLayerId = 'base' } = getUserGameSets();
      const activeLayer = convy.methods.getActiveLayer();
      
      if (activeLayer && activeLayer.info.visible) {
        layersToTransform.push(activeLayerId);
      }
    }

    return layersToTransform;
  };

  // Инициализация параметров трансформации слоя/группы
  const initLayerTransform = () => {
    if (Object.keys(zoom.initialized || {}).length > 0) {
      return;
    }

    if (!useStore.getState().canDraw) { return; }

    const layersToTransform = getLayersForTransform();
    if (layersToTransform.length === 0) {
      return;
    }

    zoom.layerMode = true;
    zoom.lastLayerTransformationStart = Date.now();
    zoom.transformingLayers = {}; // Хранилище данных для каждого трансформируемого слоя

    // Сохраняем центр жеста в экранных координатах
    const centerScreen = zoom.initialZoomCenter;

    // Инициализируем данные для каждого слоя
    layersToTransform.forEach(layerId => {
      const layer = convy.methods.getLayer(layerId);
      const { layerPoint } = drawing.methods.getLayerCoordinates(centerScreen, layerId);
      
      zoom.transformingLayers[layerId] = {
        initialOffset: { ...layer.info.offset },
        initialScale: layer.info.scale,
        initialRotation: layer.info.rotation,
        initialLayerGestureData: {
          distance: zoom.initialDistance,
          angle: zoom.initialTouchAngle,
          centerScreen: centerScreen,
          centerLayer: layerPoint,
        },
      };
    });

    zoom.initializedLayer = {}; // Сбрасываем пороги для слоя

    // Показываем подходящее сообщение
    if (convy.transformGroupId) {
      const gameSettings = info.game.settings || {};
      const layerGroups = gameSettings.layerGroups || {};
      const groupName = layerGroups[convy.transformGroupId]?.title || convy.transformGroupId;
      showTemporaryHint(window.t('customize_layer.group_transform_started', { groupName }), { force: true, duration: 2000 });
    } else {
      const activeLayer = convy.methods.getActiveLayer();
      showTemporaryHint(window.t('customize_layer.transform_started', { layerName: activeLayer.info.name}), { force: true, duration: 2000 });
    }

    if (!convy.layerTransformation) {
      convy.layerTransformation = {
        prev: layersToTransform.reduce((acc, layerId) => {
          const layer = convy.methods.getLayer(layerId);
          acc[layerId] = JSON.parse(JSON.stringify(layer.info));
          return acc;
        }, {}),
        type: 'touch',
        layersCount: layersToTransform.length,
      };
    }

    window.croco?.haptic?.();
  };

  const updateLayerTransform = (event) => {
    const { zoomFactor, rotation } = useStore.getState();
    const layersToTransform = getLayersForTransform();
    
    if (layersToTransform.length === 0 || !zoom.transformingLayers) {
      return;
    }

    // Новые параметры жеста
    const newCenterScreen = calculateCenter(event.touches[0], event.touches[1]);
    let newDistance = calculateDistance(event.touches[0], event.touches[1]);
    let newAngle = calculateAngle(event.touches[0], event.touches[1]);

    // Обрабатываем каждый слой
    layersToTransform.forEach(layerId => {
      const layer = convy.methods.getLayer(layerId);
      const layerCanvas = layer.canvas;
      const transformData = zoom.transformingLayers[layerId];
      
      if (!transformData) return;

      // Масштаб
      let scaleFactor = newDistance / transformData.initialLayerGestureData.distance;
      let newLayerScale = transformData.initialScale * scaleFactor;

      if (!zoom.initializedLayer.scale) {
        newLayerScale = transformData.initialScale; 

        if (Math.abs(scaleFactor - 1) < 0.2) {
          // В пределах порога - не изменяем
        } else {
          zoom.initializedLayer.scale = true;
          // Обновляем расстояние для всех слоев
          Object.keys(zoom.transformingLayers).forEach(id => {
            zoom.transformingLayers[id].initialLayerGestureData.distance = newDistance;
          });
        }
      }

      // Вращение
      let angleDelta = newAngle - transformData.initialLayerGestureData.angle;
      let newLayerRotation = transformData.initialRotation + angleDelta;

      if (!zoom.initializedLayer.rotation) {
        newLayerRotation = transformData.initialRotation;

        if (Math.abs(angleDelta) < 7) {
          // В пределах порога
        } else {
          zoom.initializedLayer.rotation = true;
          // Обновляем угол для всех слоев
          Object.keys(zoom.transformingLayers).forEach(id => {
            zoom.transformingLayers[id].initialLayerGestureData.angle = newAngle;
          });
        }
      }

      // Смещение от движения центра жеста в экранных координатах
      const rawOffsetDelta = {
        x: newCenterScreen.x - transformData.initialLayerGestureData.centerScreen.x,
        y: newCenterScreen.y - transformData.initialLayerGestureData.centerScreen.y,
      };

      // Применяем обратное вращение холста к смещению
      const canvasRotationRad = (rotation * Math.PI) / 180;
      const adjustedOffsetDelta = {
        x: rawOffsetDelta.x * Math.cos(-canvasRotationRad) - rawOffsetDelta.y * Math.sin(-canvasRotationRad),
        y: rawOffsetDelta.x * Math.sin(-canvasRotationRad) + rawOffsetDelta.y * Math.cos(-canvasRotationRad),
      };

      // Корректируем с учётом масштаба канваса и слоя
      const finalOffsetDelta = {
        x: adjustedOffsetDelta.x / (zoomFactor * convy.scale),
        y: adjustedOffsetDelta.y / (zoomFactor * convy.scale),
      };

      if (!zoom.initializedLayer.offset) {
        finalOffsetDelta.x = 0;
        finalOffsetDelta.y = 0;

        if (calculateDistance(newCenterScreen, transformData.initialLayerGestureData.centerScreen) < 15) {
          // В пределах порога
        } else {
          zoom.initializedLayer.offset = true;
          // Обновляем центр для всех слоев
          Object.keys(zoom.transformingLayers).forEach(id => {
            zoom.transformingLayers[id].initialLayerGestureData.centerScreen = newCenterScreen;
          });
        }
      }

      // Новое смещение с учётом инвариантности
      const newLayerOffset = calculateLayerInvariantOffset({
        initialLayerOffset: transformData.initialOffset,
        initialLayerScale: transformData.initialScale,
        newLayerScale: newLayerScale,
        initialLayerRotation: transformData.initialRotation,
        newLayerRotation: newLayerRotation,
        pivotLayerPoint: transformData.initialLayerGestureData.centerLayer,
        offsetDelta: finalOffsetDelta,
        convyScale: convy.scale,
        layerCanvas: layerCanvas,
      });

      // Применяем новые параметры к слою
      layer.info.scale = newLayerScale;
      layer.info.rotation = newLayerRotation;
      layer.info.offset = newLayerOffset;
    });

    drawing.methods.redraw();
  };

  // Функция завершения трансформации
  const finishLayerTransform = () => {

    if (!convy.layerTransformation) {
      return;
    }

    const layersToTransform = getLayersForTransform();
    
    if (layersToTransform.length === 0) {
      delete convy.layerTransformation;
      return;
    }

    const prevData = {};
    const infoData = {};

    const prev = ref.convy.layerTransformation?.prev
    
    layersToTransform.forEach(id => {
      const layer = ref.convy.methods.getLayer(id);
      if (layer) {
        infoData[id] = JSON.parse(JSON.stringify(layer.info));
        if (prev?.[id]) {
          prevData[id] = prev[id];
        }
      }
    });

    let note;
    if (convy.transformGroupId) {
      const gameSettings = info.game.settings || {};
      const layerGroups = gameSettings.layerGroups || {};
      note = `transform group: ${layerGroups[convy.transformGroupId]?.title || convy.transformGroupId}`;

    } else {
      const layer = ref.convy.methods.getLayer(layersToTransform[0]);
      note = `transform layer: ${layer?.info?.name}`;
    }

    // Создаем единый штрих для всех трансформированных слоев
    history.methods.newTransformationStroke({
      layerIds: layersToTransform,
      prev: prevData,
      info: infoData,
      note,
      groupId: convy.transformGroupId,
    });

    // Очищаем временные данные
    delete convy.layerTransformation;
    delete zoom.transformingLayers;
    work.methods.saveGameSets({ layerInfo: convy.layerInfo });
  };

  return {
    updateLayerTransform,
    initLayerTransform,
    finishLayerTransform,
  };
};